import { useState, useRef, lazy, Suspense } from 'react';
const TwoFactorSetup = lazy(() => import('./components/TwoFactorSetup'));
const SecurityTimeline = lazy(() => import('./components/SecurityTimeline'));
const SessionManager = lazy(() => import('./components/SessionManager'));
import { motion } from 'framer-motion';
import AppLayout from '@/components/feature/AppLayout';
import { useAuthContext } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/lib/supabase';

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.4, delay },
});

type TabKey = 'profile' | 'password' | 'security' | 'activity' | 'sessions';

export default function AccountPage() {
  const { user } = useAuthContext();
  const { profile, updateProfile } = useProfile();
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [activeTab, setActiveTab] = useState<TabKey>('profile');

  /* ── Profile state ── */
  const [profileForm, setProfileForm] = useState({
    username: user?.username ?? '',
    email: user?.email ?? '',
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState(false);
  const [profileError, setProfileError] = useState('');

  /* ── Password state ── */
  const [pwForm, setPwForm] = useState({ newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwSuccess, setPwSuccess] = useState(false);
  const [pwError, setPwError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  /* ── Avatar state ── */
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const [avatarSuccess, setAvatarSuccess] = useState(false);

  const avatarUrl = profile?.avatar_url ?? null;

  const handleAvatarClick = () => avatarInputRef.current?.click();

  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be under 5 MB.');
      return;
    }
    setAvatarError('');
    setAvatarUploading(true);

    const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
    const path = `${user.id}/avatar.${ext}`;

    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(path, file, { upsert: true, contentType: file.type });

    if (uploadError) {
      setAvatarError(uploadError.message);
      setAvatarUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(path);
    const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
    await updateProfile({ avatar_url: publicUrl });
    setAvatarUploading(false);
    setAvatarSuccess(true);
    setTimeout(() => setAvatarSuccess(false), 3000);
    // Reset input
    if (avatarInputRef.current) avatarInputRef.current.value = '';
  };

  const initials = (profileForm.username || 'U').slice(0, 2).toUpperCase();

  const joinDate = profile?.joined_at
    ? new Date(profile.joined_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  /* ── Handlers ── */
  const handleProfileSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setProfileError('');
    if (!profileForm.username.trim() || !profileForm.email.trim()) return;
    setProfileSaving(true);
    const { error } = await supabase.auth.updateUser({
      email: profileForm.email.trim() !== user?.email ? profileForm.email.trim() : undefined,
      data: { username: profileForm.username.trim() },
    });
    setProfileSaving(false);
    if (error) {
      setProfileError(error.message);
      return;
    }
    setProfileSuccess(true);
    setTimeout(() => setProfileSuccess(false), 3000);
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwError('');
    if (pwForm.newPw.length < 6) { setPwError('Password must be at least 6 characters.'); return; }
    if (pwForm.newPw !== pwForm.confirm) { setPwError('Passwords do not match.'); return; }
    setPwSaving(true);
    const { error } = await supabase.auth.updateUser({ password: pwForm.newPw });
    setPwSaving(false);
    if (error) { setPwError(error.message); return; }
    setPwSuccess(true);
    setPwForm({ newPw: '', confirm: '' });
    setTimeout(() => setPwSuccess(false), 3500);
  };

  const tabs: { key: TabKey; label: string; icon: string }[] = [
    { key: 'profile', label: 'Profile', icon: 'ri-user-3-line' },
    { key: 'password', label: 'Password', icon: 'ri-lock-password-line' },
    { key: 'security', label: 'Security', icon: 'ri-shield-check-line' },
    { key: 'activity', label: 'Activity', icon: 'ri-history-line' },
    { key: 'sessions', label: 'Sessions', icon: 'ri-device-line' },
  ];

  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto space-y-6">

        {/* Header card */}
        <motion.div {...fadeUp()} className="bg-sidebar rounded-2xl p-6 flex flex-col sm:flex-row items-center sm:items-start gap-5 overflow-hidden relative">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-brand-400 via-transparent to-transparent pointer-events-none" />

          {/* Clickable avatar */}
          <div className="relative shrink-0 group cursor-pointer" onClick={handleAvatarClick}>
            <div className="w-20 h-20 rounded-2xl bg-brand-500/25 border-2 border-brand-400/40 flex items-center justify-center overflow-hidden">
              {avatarUrl ? (
                <img src={avatarUrl} alt="Avatar" className="w-full h-full object-cover" />
              ) : (
                <span className="text-3xl font-extrabold text-brand-300">{initials}</span>
              )}
            </div>
            <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              {avatarUploading ? (
                <i className="ri-loader-4-line animate-spin text-white text-xl" />
              ) : (
                <i className="ri-camera-line text-white text-xl" />
              )}
            </div>
          </div>

          <input
            ref={avatarInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleAvatarUpload}
          />

          <div className="text-center sm:text-left">
            <h2 className="text-xl font-bold text-white">{profileForm.username || 'User'}</h2>
            <p className="text-sm text-white/50 mt-0.5">{profileForm.email}</p>

            {avatarError && (
              <p className="text-xs text-red-400 mt-1">{avatarError}</p>
            )}
            {avatarSuccess && (
              <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-xs text-green-400 mt-1 flex items-center gap-1">
                <i className="ri-checkbox-circle-line" />Avatar updated!
              </motion.p>
            )}
            {!avatarError && !avatarSuccess && (
              <p className="text-xs text-white/30 mt-1">Click avatar to change photo</p>
            )}

            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
              <span className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1 rounded-full ${user?.role === 'admin' ? 'bg-brand-500/30 text-brand-300 border border-brand-400/30' : 'bg-white/10 text-white/60 border border-white/20'}`}>
                <i className={user?.role === 'admin' ? 'ri-shield-star-line' : 'ri-user-line'} />
                {user?.role === 'admin' ? 'Administrator' : 'User'}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-white/40 border border-white/10 px-3 py-1 rounded-full">
                <i className="ri-calendar-line" />Member since {joinDate}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[11px] text-green-400 border border-green-400/20 px-3 py-1 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />Active
              </span>
              {profile && profile.api_calls_total > 0 && (
                <span className="inline-flex items-center gap-1.5 text-[11px] text-brand-300 border border-brand-400/20 px-3 py-1 rounded-full">
                  <i className="ri-terminal-line" />{profile.api_calls_total.toLocaleString()} API calls
                </span>
              )}
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <motion.div {...fadeUp(0.08)} className="bg-white rounded-2xl overflow-hidden">
          <div className="flex border-b border-gray-100 px-2 pt-2 gap-1 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-t-lg cursor-pointer whitespace-nowrap transition-all ${
                  activeTab === tab.key
                    ? 'bg-brand-50 text-brand-600 border-b-2 border-brand-500'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <i className={`${tab.icon} text-base`} />
                {tab.label}
              </button>
            ))}
          </div>

          <div className="p-6">

            {/* ── Profile Tab ── */}
            {activeTab === 'profile' && (
              <form onSubmit={handleProfileSave} className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Profile Information</h3>
                  <p className="text-xs text-gray-400">Update your display name and email address.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Username</label>
                    <input
                      type="text"
                      value={profileForm.username}
                      onChange={e => setProfileForm(f => ({ ...f, username: e.target.value }))}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                      placeholder="yourname"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email Address</label>
                    <input
                      type="email"
                      value={profileForm.email}
                      onChange={e => setProfileForm(f => ({ ...f, email: e.target.value }))}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 focus:outline-none focus:ring-2 focus:ring-brand-400"
                      placeholder="you@example.com"
                    />
                    <p className="text-[10px] text-gray-400 mt-1">Changing email will require verification.</p>
                  </div>
                </div>

                {/* Read-only role field - users cannot edit this */}
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1.5 block">Account Role</label>
                  <div className="flex items-center gap-2 px-3 py-2.5 bg-gray-100 border border-gray-200 rounded-lg cursor-not-allowed">
                    <i className={`${user?.role === 'admin' ? 'ri-shield-star-line text-brand-500' : 'ri-user-line text-gray-400'} text-base`} />
                    <span className="text-sm text-gray-700 capitalize">{user?.role ?? 'user'}</span>
                    <span className="ml-auto text-[11px] text-gray-400">Contact admin to change</span>
                  </div>
                </div>

                {profileError && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5"
                  >
                    <i className="ri-error-warning-fill text-red-500" />
                    {profileError}
                  </motion.div>
                )}

                {profileSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-2.5"
                  >
                    <i className="ri-checkbox-circle-fill text-green-500" />
                    Profile updated successfully!
                  </motion.div>
                )}

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={profileSaving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {profileSaving ? (
                      <><i className="ri-loader-4-line animate-spin" />Saving…</>
                    ) : (
                      <><i className="ri-save-line" />Save Changes</>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* ── Password Tab ── */}
            {activeTab === 'password' && (
              <form onSubmit={handlePasswordChange} className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Change Password</h3>
                  <p className="text-xs text-gray-400">Choose a new secure password for your account.</p>
                </div>

                {pwError && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-4 py-2.5"
                  >
                    <i className="ri-error-warning-fill text-red-500" />
                    {pwError}
                  </motion.div>
                )}

                {pwSuccess && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-2 text-sm text-green-700 bg-green-50 border border-green-100 rounded-lg px-4 py-2.5"
                  >
                    <i className="ri-checkbox-circle-fill text-green-500" />
                    Password changed successfully!
                  </motion.div>
                )}

                {[
                  { label: 'New Password', field: 'newPw' as const, show: showNew, toggle: () => setShowNew(v => !v), value: pwForm.newPw },
                  { label: 'Confirm New Password', field: 'confirm' as const, show: showConfirm, toggle: () => setShowConfirm(v => !v), value: pwForm.confirm },
                ].map(({ label, field, show, toggle, value }) => (
                  <div key={field}>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">{label}</label>
                    <div className="relative">
                      <input
                        type={show ? 'text' : 'password'}
                        value={value}
                        onChange={e => setPwForm(f => ({ ...f, [field]: e.target.value }))}
                        className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2.5 pr-10 focus:outline-none focus:ring-2 focus:ring-brand-400"
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        onClick={toggle}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={show ? 'ri-eye-off-line' : 'ri-eye-line'} />
                      </button>
                    </div>
                    {field === 'newPw' && value.length > 0 && (
                      <div className="flex items-center gap-2 mt-2">
                        {[
                          { label: '6+ chars', ok: value.length >= 6 },
                          { label: 'Uppercase', ok: /[A-Z]/.test(value) },
                          { label: 'Number', ok: /\d/.test(value) },
                        ].map(rule => (
                          <span
                            key={rule.label}
                            className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${rule.ok ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}
                          >
                            {rule.ok ? <i className="ri-check-line mr-0.5" /> : null}{rule.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}

                <div className="flex justify-end pt-1">
                  <button
                    type="submit"
                    disabled={pwSaving}
                    className="flex items-center gap-2 px-6 py-2.5 bg-brand-500 hover:bg-brand-600 disabled:opacity-60 text-white text-sm font-semibold rounded-lg cursor-pointer whitespace-nowrap transition-colors"
                  >
                    {pwSaving ? (
                      <><i className="ri-loader-4-line animate-spin" />Updating…</>
                    ) : (
                      <><i className="ri-lock-password-line" />Update Password</>
                    )}
                  </button>
                </div>
              </form>
            )}

            {/* ── Security Tab ── */}
            {activeTab === 'security' && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Security Overview</h3>
                  <p className="text-xs text-gray-400">Review your account security and manage two-factor authentication.</p>
                </div>
                <div className="space-y-3">
                  {[
                    { label: 'Email verified', ok: true, icon: 'ri-mail-check-line', desc: 'Your email address is confirmed.' },
                    { label: 'Password set', ok: true, icon: 'ri-lock-line', desc: 'Account secured with a password.' },
                    { label: 'API key rotation', ok: true, icon: 'ri-refresh-line', desc: 'Keys can be rotated at any time from Developer Hub.' },
                  ].map(item => (
                    <div key={item.label} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100">
                      <div className="w-9 h-9 rounded-lg bg-green-50 flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-base text-green-500`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-800">{item.label}</div>
                        <div className="text-xs text-gray-400 mt-0.5">{item.desc}</div>
                      </div>
                      <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full shrink-0 bg-green-50 text-green-600">Enabled</span>
                    </div>
                  ))}
                </div>
                <div className="border border-gray-100 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                      <i className="ri-smartphone-line text-base text-brand-500" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-800">Two-Factor Authentication (2FA)</div>
                      <div className="text-xs text-gray-400">Protect your account with Google Authenticator TOTP</div>
                    </div>
                  </div>
                  <Suspense fallback={<div className="h-20 bg-gray-50 rounded-xl animate-pulse" />}>
                    <TwoFactorSetup />
                  </Suspense>
                </div>
              </div>
            )}

            {/* ── Activity Tab ── */}
            {activeTab === 'activity' && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Security Activity</h3>
                  <p className="text-xs text-gray-400">Recent security events on your account — 2FA changes, password updates, session activity.</p>
                </div>
                <Suspense fallback={
                  <div className="space-y-3 animate-pulse">
                    {[...Array(4)].map((_, i) => (
                      <div key={i} className="flex gap-3 items-start">
                        <div className="w-9 h-9 rounded-xl bg-gray-100 shrink-0" />
                        <div className="flex-1 space-y-1.5 pt-1">
                          <div className="h-3.5 bg-gray-100 rounded w-1/3" />
                          <div className="h-3 bg-gray-100 rounded w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                }>
                  <SecurityTimeline />
                </Suspense>
              </div>
            )}

            {/* ── Sessions Tab ── */}
            {activeTab === 'sessions' && (
              <div className="space-y-5">
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">Active Sessions</h3>
                  <p className="text-xs text-gray-400">Devices and browsers currently signed in to your account. Revoke any session you don&apos;t recognize.</p>
                </div>
                <Suspense fallback={
                  <div className="space-y-3 animate-pulse">
                    {[...Array(2)].map((_, i) => (
                      <div key={i} className="h-20 bg-gray-100 rounded-xl" />
                    ))}
                  </div>
                }>
                  <SessionManager />
                </Suspense>
              </div>
            )}

          </div>
        </motion.div>
      </div>
    </AppLayout>
  );
}
